/**
 * Ambient occlusion, baked into the cabin's own vertices at start-up.
 *
 * ## Why an enclosed box needs this more than anything else
 *
 * The cabin is lit by one sun and a hemisphere fill. A hemisphere light shades purely by surface
 * *normal*, so every inward-facing wall in the cockpit — which is most of it — collects the same
 * value regardless of whether it is beside a window or wedged in the corner behind the pedals.
 * The result is an interior of one flat grey, which is exactly what the first screenshots of this
 * cabin show, and it is why the procedural grain maps added alongside it were invisible: there was
 * no light gradient for them to perturb.
 *
 * Real interiors read as interiors because of *occlusion*, not because of texture. The corner where
 * two walls meet is dark because most of the sky it could see is blocked by the walls themselves.
 * That is a property of the geometry, not of the light, so it can be computed once — and this
 * geometry is procedural, so we know it exactly.
 *
 * ## Why vertex colours rather than an AO map
 *
 * An `aoMap` needs a second UV set and a baked texture, which for procedural geometry means
 * generating an atlas, packing it, and shipping or regenerating it. The cabin is a thousand-odd
 * vertices; a `color` attribute costs three floats each and multiplies straight into the albedo
 * with no second UV channel, no atlas, and nothing to keep in sync with the shape constants.
 *
 * The cost is resolution — AO varies linearly across a triangle — which is why the large panels are
 * subdivided by `aoSegments` in `cockpitShell.ts` and the small parts are not.
 *
 * ## Why this does not use `Raycaster`
 *
 * It did, and it took **2.1 seconds** for one cabin. `Raycaster.intersectObjects` is built for
 * picking: it collects *every* intersection along the ray, allocates a result array, and sorts by
 * distance. This query only ever asks "is there anything at all", 33,000 times, so all of that work
 * is thrown away — 62 µs per ray, against a budget where the whole app is flyable in 0.8 s.
 *
 * What replaces it is a uniform grid over the cabin's triangles and a 3D-DDA walk with an early
 * exit on the first hit. Same answer, and the cabin is a box about a metre and a half across, so a
 * grid is exactly the right structure: no build cost worth measuring, no tree, and the occupancy is
 * even because the geometry is walls.
 *
 * `updateFixed` / `updateFrame`: neither. Run once per cabin, at build time.
 */

import { BufferAttribute, Matrix3, Vector3, type Material, type Mesh } from 'three';

/**
 * How many rays leave each vertex.
 *
 * The output is multiplied into an albedo and then interpolated across a triangle, so the eye is
 * judging a smooth gradient rather than a noise floor — this can be far lower than a renderer's AO
 * pass would need. 16 samples over the measured 700–1,400 vertices is a few milliseconds.
 */
const RAYS_PER_VERTEX = 16;

/**
 * How dark the deepest corner is allowed to get.
 *
 * Not zero, and not low. A fully enclosed vertex still receives light bounced off everything around
 * it, and this term is standing in for that bounce as well as for the occlusion — there is no
 * global illumination in this renderer, so whatever inter-reflection a cabin has has to be here or
 * nowhere. An interior corner that renders pure black is a rendering artefact, not a dark corner.
 *
 * It was 0.35, chosen before there was anything to measure it against. Applied to the cabin alone
 * that gave a mean occlusion of 0.64; extended over the whole cockpit — panel, coaming, yoke,
 * levers, pedals, all of which live in the enclosed lower half — the mean fell to **0.447**, which
 * is a 55 % darkening of an interior that was already short of light. 0.55 puts the mean back near
 * 0.62 while keeping the full 0.55–1.0 range, so the gradient survives and the cockpit does not
 * turn into a cave.
 */
const MIN_OCCLUSION = 0.55;

/**
 * How far a ray travels before it counts as having escaped [m].
 *
 * The cabin's longest interior run is the 1.44 m from the windscreen base to the aft bulkhead, so a
 * ray still travelling at 1.8 m has left through a window and is looking at sky. It was 2.5, which
 * bought nothing — no cabin geometry is out there — and cost directly, because this bounds the DDA
 * walk and therefore the whole bake.
 *
 * Shorter than 1.44 would be wrong rather than merely cheaper: a ray fired down the length of the
 * cabin would be scored as having escaped, and the far end of the footwell would brighten.
 */
const ESCAPE_DISTANCE_M = 1.8;

/** Offset along the normal before casting, so a vertex does not occlude itself. */
const SURFACE_EPSILON_M = 0.002;

/**
 * Target grid cell size [m].
 *
 * 0.05, and the number followed the geometry rather than the other way round. At 0.12 — a hand's
 * width — a cell held a few triangles while the cabin was 1,300 bare walls, and the bake ran at
 * 1.4 µs per ray. Once the panel gained switches, breakers, placards and instrument bezels the
 * triangle count went to 7,400 *concentrated in one corner*, so the cells over the panel held
 * dozens each and the same walk cost 11 µs. A grid's whole value is that a cell is small enough to
 * be nearly empty, and "nearly empty" is a property of the densest region, not the average one.
 */
const CELL_SIZE_M = 0.05;

// --- the occluder grid ---------------------------------------------------------------------------

/**
 * Every triangle in the cabin, in a uniform grid.
 *
 * Triangles are stored once, flat, as nine floats; the grid holds indices into that array. A
 * triangle spanning several cells is listed in each, which is why `seen` below exists — a long wall
 * would otherwise be tested once per cell the ray crosses.
 */
class TriangleGrid {
  private readonly triangles: Float32Array;
  private readonly cells: Int32Array[];
  private readonly min = new Vector3();
  private readonly divisions = new Vector3();
  private readonly cellSize = new Vector3();
  /** Last ray id each triangle was tested against, so a triangle is tested at most once per ray. */
  private readonly seen: Int32Array;
  private rayId = 0;

  constructor(meshes: readonly Mesh[]) {
    let triangleCount = 0;
    for (const mesh of meshes) triangleCount += (mesh.geometry.getIndex()?.count ?? 0) / 3;
    this.triangles = new Float32Array(triangleCount * 9);
    this.seen = new Int32Array(triangleCount).fill(-1);

    const max = new Vector3(-Infinity, -Infinity, -Infinity);
    this.min.set(Infinity, Infinity, Infinity);

    let write = 0;
    const vertex = new Vector3();
    for (const mesh of meshes) {
      const position = mesh.geometry.getAttribute('position');
      const index = mesh.geometry.getIndex();
      if (index === null) continue;
      /*
       * Everything is gathered in the cockpit's frame, not each mesh's own.
       *
       * The shell's geometry is already baked into cabin coordinates, but the panel, the coaming
       * and the yoke are meshes carrying their own position and rotation — the panel alone is
       * tilted 12° back. Reading their local attributes and ignoring that would build a grid of
       * correctly-shaped parts stacked on top of each other at the origin, which occludes
       * everything and would render the whole cockpit uniformly dark. That is a failure that looks
       * like a plausible result, so it is worth naming.
       */
      for (let i = 0; i < index.count; i++) {
        const v = index.getX(i);
        vertex.set(position.getX(v), position.getY(v), position.getZ(v));
        vertex.applyMatrix4(mesh.matrixWorld);
        const x = vertex.x;
        const y = vertex.y;
        const z = vertex.z;
        this.triangles[write++] = x;
        this.triangles[write++] = y;
        this.triangles[write++] = z;
        this.min.set(Math.min(this.min.x, x), Math.min(this.min.y, y), Math.min(this.min.z, z));
        max.set(Math.max(max.x, x), Math.max(max.y, y), Math.max(max.z, z));
      }
    }

    // Pad, so a triangle exactly on the boundary is inside the grid rather than on its edge.
    this.min.addScalar(-0.01);
    max.addScalar(0.01);
    const span = max.clone().sub(this.min);
    this.divisions.set(
      Math.max(1, Math.ceil(span.x / CELL_SIZE_M)),
      Math.max(1, Math.ceil(span.y / CELL_SIZE_M)),
      Math.max(1, Math.ceil(span.z / CELL_SIZE_M)),
    );
    this.cellSize.set(
      span.x / this.divisions.x,
      span.y / this.divisions.y,
      span.z / this.divisions.z,
    );

    // Two passes: count per cell, then fill. Avoids growing a few thousand JS arrays.
    const cellCount = this.divisions.x * this.divisions.y * this.divisions.z;
    const counts = new Int32Array(cellCount);
    for (let t = 0; t < triangleCount; t++) this.forEachCell(t, (cell) => void counts[cell]++);
    this.cells = new Array(cellCount);
    for (let c = 0; c < cellCount; c++) this.cells[c] = new Int32Array(counts[c]);
    counts.fill(0);
    for (let t = 0; t < triangleCount; t++) {
      this.forEachCell(t, (cell) => {
        this.cells[cell][counts[cell]++] = t;
      });
    }
  }

  /** Visit every cell a triangle's bounding box touches. */
  private forEachCell(triangle: number, visit: (cell: number) => void): void {
    const base = triangle * 9;
    let minX = Infinity;
    let minY = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let maxZ = -Infinity;
    for (let v = 0; v < 3; v++) {
      const x = this.triangles[base + v * 3];
      const y = this.triangles[base + v * 3 + 1];
      const z = this.triangles[base + v * 3 + 2];
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (z < minZ) minZ = z;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
      if (z > maxZ) maxZ = z;
    }
    const x0 = this.cellIndex(minX, this.min.x, this.cellSize.x, this.divisions.x);
    const x1 = this.cellIndex(maxX, this.min.x, this.cellSize.x, this.divisions.x);
    const y0 = this.cellIndex(minY, this.min.y, this.cellSize.y, this.divisions.y);
    const y1 = this.cellIndex(maxY, this.min.y, this.cellSize.y, this.divisions.y);
    const z0 = this.cellIndex(minZ, this.min.z, this.cellSize.z, this.divisions.z);
    const z1 = this.cellIndex(maxZ, this.min.z, this.cellSize.z, this.divisions.z);
    for (let z = z0; z <= z1; z++) {
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          visit((z * this.divisions.y + y) * this.divisions.x + x);
        }
      }
    }
  }

  private cellIndex(value: number, min: number, size: number, divisions: number): number {
    const i = Math.floor((value - min) / size);
    return i < 0 ? 0 : i >= divisions ? divisions - 1 : i;
  }

  /**
   * Is anything in the way? A 3D-DDA walk, stopping at the first triangle hit.
   *
   * `Ray.intersectTriangle` is not used, and not for speed alone: it needs a `Vector3` for the hit
   * point it then discards, and this is the inner loop of thirty thousand queries.
   */
  occluded(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): boolean {
    const id = this.rayId++;

    let cx = this.cellIndex(ox, this.min.x, this.cellSize.x, this.divisions.x);
    let cy = this.cellIndex(oy, this.min.y, this.cellSize.y, this.divisions.y);
    let cz = this.cellIndex(oz, this.min.z, this.cellSize.z, this.divisions.z);

    // Anything starting outside the grid is looking at open sky before it looks at the cabin.
    if (
      ox < this.min.x - 0.02 ||
      oy < this.min.y - 0.02 ||
      oz < this.min.z - 0.02 ||
      cx >= this.divisions.x ||
      cy >= this.divisions.y ||
      cz >= this.divisions.z
    ) {
      return false;
    }

    const stepX = dx > 0 ? 1 : -1;
    const stepY = dy > 0 ? 1 : -1;
    const stepZ = dz > 0 ? 1 : -1;
    const deltaX = Math.abs(this.cellSize.x / (dx || 1e-12));
    const deltaY = Math.abs(this.cellSize.y / (dy || 1e-12));
    const deltaZ = Math.abs(this.cellSize.z / (dz || 1e-12));

    // Distance along the ray to the first cell boundary on each axis.
    const boundaryX = this.min.x + (cx + (stepX > 0 ? 1 : 0)) * this.cellSize.x;
    const boundaryY = this.min.y + (cy + (stepY > 0 ? 1 : 0)) * this.cellSize.y;
    const boundaryZ = this.min.z + (cz + (stepZ > 0 ? 1 : 0)) * this.cellSize.z;
    let nextX = dx === 0 ? Infinity : (boundaryX - ox) / dx;
    let nextY = dy === 0 ? Infinity : (boundaryY - oy) / dy;
    let nextZ = dz === 0 ? Infinity : (boundaryZ - oz) / dz;

    let travelled = 0;
    while (travelled <= ESCAPE_DISTANCE_M) {
      const cell = this.cells[(cz * this.divisions.y + cy) * this.divisions.x + cx];
      for (let i = 0; i < cell.length; i++) {
        const triangle = cell[i];
        if (this.seen[triangle] === id) continue;
        this.seen[triangle] = id;
        if (this.hits(triangle, ox, oy, oz, dx, dy, dz)) return true;
      }

      if (nextX < nextY && nextX < nextZ) {
        cx += stepX;
        travelled = nextX;
        nextX += deltaX;
        if (cx < 0 || cx >= this.divisions.x) return false;
      } else if (nextY < nextZ) {
        cy += stepY;
        travelled = nextY;
        nextY += deltaY;
        if (cy < 0 || cy >= this.divisions.y) return false;
      } else {
        cz += stepZ;
        travelled = nextZ;
        nextZ += deltaZ;
        if (cz < 0 || cz >= this.divisions.z) return false;
      }
    }
    return false;
  }

  /** Möller–Trumbore, written out in scalars because this is the hot loop. */
  private hits(
    triangle: number,
    ox: number,
    oy: number,
    oz: number,
    dx: number,
    dy: number,
    dz: number,
  ): boolean {
    const t = triangle * 9;
    const ax = this.triangles[t];
    const ay = this.triangles[t + 1];
    const az = this.triangles[t + 2];
    const e1x = this.triangles[t + 3] - ax;
    const e1y = this.triangles[t + 4] - ay;
    const e1z = this.triangles[t + 5] - az;
    const e2x = this.triangles[t + 6] - ax;
    const e2y = this.triangles[t + 7] - ay;
    const e2z = this.triangles[t + 8] - az;

    const px = dy * e2z - dz * e2y;
    const py = dz * e2x - dx * e2z;
    const pz = dx * e2y - dy * e2x;
    const determinant = e1x * px + e1y * py + e1z * pz;
    // Two-sided: the cabin's walls are single-sided boxes and a ray leaving the interior would
    // otherwise pass straight out through the back of one.
    if (determinant > -1e-9 && determinant < 1e-9) return false;

    const inverse = 1 / determinant;
    const tx = ox - ax;
    const ty = oy - ay;
    const tz = oz - az;
    const u = (tx * px + ty * py + tz * pz) * inverse;
    if (u < 0 || u > 1) return false;

    const qx = ty * e1z - tz * e1y;
    const qy = tz * e1x - tx * e1z;
    const qz = tx * e1y - ty * e1x;
    const v = (dx * qx + dy * qy + dz * qz) * inverse;
    if (v < 0 || u + v > 1) return false;

    const distance = (e2x * qx + e2y * qy + e2z * qz) * inverse;
    return distance > 1e-6 && distance <= ESCAPE_DISTANCE_M;
  }
}

// --- the bake ------------------------------------------------------------------------------------

/** Fixed, evenly-spread hemisphere directions in tangent space. Built once. */
let hemisphere: Float32Array | null = null;

function hemisphereDirections(): Float32Array {
  if (hemisphere !== null) return hemisphere;
  const directions = new Float32Array(RAYS_PER_VERTEX * 3);
  // The golden-angle spiral: `z` walks the hemisphere while the azimuth advances by an irrational
  // fraction of a turn, so no two samples line up however many are taken. Deterministic, which
  // also keeps this off `Math.random` — non-negotiable #7 routes randomness through one seeded
  // generator and an occlusion bake is not a place that needs any.
  const golden = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < RAYS_PER_VERTEX; i++) {
    // Cosine weighting: `z` is the square root of a uniform, so sample density already carries the
    // cosine term the rendering equation wants and no per-sample weight is needed below.
    const z = Math.sqrt((i + 0.5) / RAYS_PER_VERTEX);
    const radius = Math.sqrt(1 - z * z);
    const theta = i * golden;
    directions[i * 3] = Math.cos(theta) * radius;
    directions[i * 3 + 1] = Math.sin(theta) * radius;
    directions[i * 3 + 2] = z;
  }
  hemisphere = directions;
  return directions;
}

const normal = /* #__PURE__ */ new Vector3();
const tangent = /* #__PURE__ */ new Vector3();
const bitangent = /* #__PURE__ */ new Vector3();
const AXIS_X = /* #__PURE__ */ new Vector3(1, 0, 0);
const AXIS_Y = /* #__PURE__ */ new Vector3(0, 1, 0);

/**
 * Write an occlusion term into every mesh's `color` attribute.
 *
 * All meshes occlude all meshes: the cabin is one enclosure, and a seat back shadowing the bulkhead
 * behind it is exactly the kind of contact the eye reads as "these two things are in the same room".
 *
 * @returns how long it took [ms], so the caller can log or assert on it.
 */
export function bakeCabinOcclusion(meshes: readonly Mesh[]): number {
  const started = performance.now();
  const directions = hemisphereDirections();
  for (const mesh of meshes) mesh.updateWorldMatrix(true, false);
  const grid = new TriangleGrid(meshes);
  const normalMatrix = new Matrix3();
  const point = new Vector3();

  for (const mesh of meshes) {
    /*
     * Everything occludes; only what will be *read* is computed.
     *
     * A gauge face is an internally-lit disc, its glass is an additive quad, and a placard is white
     * paint on a plate — none of their materials set `vertexColors`, so an occlusion term written
     * into them is a buffer nothing samples. They still have to sit in the grid above, because a
     * bezel absolutely does shadow the panel around it.
     *
     * This is most of the cost, not a rounding: the eight instrument bezels alone are 1,600 of the
     * cockpit's 6,100 vertices, and they are 2 cm rings that could not show a gradient anyway.
     */
    if (!readsVertexColours(mesh.material)) continue;

    const geometry = mesh.geometry;
    const positions = geometry.getAttribute('position');
    const normals = geometry.getAttribute('normal');
    const count = positions.count;
    const colors = new Float32Array(count * 3);
    // Normals do not transform by the model matrix — a non-uniform scale would shear them off the
    // surface. Several parts here are scaled boxes, so this is load-bearing rather than ritual.
    normalMatrix.getNormalMatrix(mesh.matrixWorld);

    for (let i = 0; i < count; i++) {
      normal
        .set(normals.getX(i), normals.getY(i), normals.getZ(i))
        .applyMatrix3(normalMatrix)
        .normalize();
      point.set(positions.getX(i), positions.getY(i), positions.getZ(i));
      point.applyMatrix4(mesh.matrixWorld);
      const ox = point.x + normal.x * SURFACE_EPSILON_M;
      const oy = point.y + normal.y * SURFACE_EPSILON_M;
      const oz = point.z + normal.z * SURFACE_EPSILON_M;

      // An orthonormal basis about the normal. The seed axis is chosen to avoid the degenerate
      // parallel case, which on axis-aligned boxes is not a rare corner — it is most of them.
      tangent.copy(Math.abs(normal.y) > 0.9 ? AXIS_X : AXIS_Y).cross(normal).normalize();
      bitangent.copy(normal).cross(tangent);

      let open = 0;
      for (let r = 0; r < RAYS_PER_VERTEX; r++) {
        const a = directions[r * 3];
        const b = directions[r * 3 + 1];
        const c = directions[r * 3 + 2];
        const dx = tangent.x * a + bitangent.x * b + normal.x * c;
        const dy = tangent.y * a + bitangent.y * b + normal.y * c;
        const dz = tangent.z * a + bitangent.z * b + normal.z * c;
        if (!grid.occluded(ox, oy, oz, dx, dy, dz)) open++;
      }

      const occlusion = MIN_OCCLUSION + (1 - MIN_OCCLUSION) * (open / RAYS_PER_VERTEX);
      colors[i * 3] = occlusion;
      colors[i * 3 + 1] = occlusion;
      colors[i * 3 + 2] = occlusion;
    }

    geometry.setAttribute('color', new BufferAttribute(colors, 3));
  }

  return performance.now() - started;
}

/** Does anything sample this material's `color` attribute? Handles the multi-material case. */
function readsVertexColours(material: Material | Material[]): boolean {
  if (Array.isArray(material)) return material.some(readsVertexColours);
  return (material as { vertexColors?: boolean }).vertexColors === true;
}
